import React, { useEffect, useMemo, useState, useContext } from 'react';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { TextField, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import TranslationProjectPreTranslateSummary from './TranslationProjectPreTranslateSummary';
import TranslationProjectPreTranslateActions from './TranslationProjectPreTranslateActions';
import { IFields } from 'CMFW/grid/fields';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import { translationItemInfoRenderer } from 'CMFW/grid/cellRenderers/translationItemInfo';
import { translationConfidenceRenderer } from 'CMFW/grid/cellRenderers/translationConfidence';
import { translationReasoningRenderer } from 'CMFW/grid/cellRenderers/translationReasoning';
import { validationStatusRenderer } from 'CMFW/grid/cellRenderers/validationStatus';
import TranslationProjectModel from 'models/TranslationProjectModel';
import TranslationProjectPreTranslateModel from 'models/TranslationProjectPreTranslateModel';
import TranslationProjectTranslationItemsModel from 'models/TranslationProjectTranslationItemsModel';
import { ITranslationProjectTranslationItem } from 'types/translationProject';
import { IRefIds } from 'constants/refs';
import { AsyncTaskStatus } from 'constants/conceptLinkerValidatorStatus';
import IdButton from 'components/IdButton';

interface IProps {
  projectId: number;
  projectName: string;
  itemsModel: TranslationProjectTranslationItemsModel;
  forceRender: () => void;
}

// Status constants
const STATUS_PENDING = 0;
const STATUS_DOING = 1;

let currentFilter: { type: string; value: string } | null = null;
let translationTextFilter = '';

const TranslationProjectPreTranslateDetails: React.FC<IProps> = ({ projectId, projectName, itemsModel, forceRender }) => {
  // Get parent model from context (for actions that affect the project)
  const model = useContext(ModuleContext) as TranslationProjectModel;

  const [activeFilter, setActiveFilter] = useState<{ type: string; value: string } | null>(null);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  currentFilter = activeFilter;

  // Create pretranslate model for actions
  const preTranslateModel = useMemo(
    () =>
      new TranslationProjectPreTranslateModel({
        render: forceRender,
        projectId,
        onDone: async () => {
          await Promise.all([model.reloadItem(), itemsModel.reload()]);
        },
      }),
    [forceRender, projectId, model, itemsModel],
  );

  const awaitingMemoryCount = useMemo(() => {
    if (!itemsModel.gridData) return 0;
    return itemsModel.gridData.filter(
      (item: ITranslationProjectTranslationItem) => item.async_task_status === AsyncTaskStatus.AWAITING_MEMORY,
    ).length;
  }, [itemsModel.gridData]);

  useEffect(() => {
    if (projectId > 0) {
      itemsModel.setProjectId(projectId);
      setActiveFilter(null);
    }
  }, [projectId, itemsModel]);

  useEffect(() => {
    if (itemsModel.loadingGrid) {
      return;
    }

    itemsModel.loadingGrid = true;
    itemsModel.render();
    const timeoutId = window.setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeFilter, itemsModel]);

  // Debounce text filter input
  useEffect(() => {
    const timer = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(timer);
  }, [filterInput]);

  // Update module-level variable and trigger grid re-filtering
  useEffect(() => {
    if (itemsModel.loadingGrid) {
      return;
    }

    translationTextFilter = filterText;
    itemsModel.loadingGrid = true;
    itemsModel.render();
    const timeoutId = window.setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [filterText, itemsModel]);

  const isFilterActive = () => currentFilter !== null || translationTextFilter.trim() !== '';

  const isRowVisible = (node: RowNode) => {
    const data = node.data as ITranslationProjectTranslationItem | undefined;
    if (!data) return true;

    // Handle categorical filter (memory/confidence levels/validated/status)
    if (currentFilter) {
      // Handle decision filter (memory/confidence levels/validated)
      if (currentFilter.type === 'decision') {
        // Validated filter
        if (currentFilter.value === 'validated') {
          if (data.is_validated !== true) return false;
        }

        // Awaiting memory filter
        else if (currentFilter.value === 'awaitingMemory') {
          if (data.async_task_status !== AsyncTaskStatus.AWAITING_MEMORY) return false;
        }

        // Memory match filter (only exact matches with similarity >= 100)
        else if (currentFilter.value === 'memory') {
          const similarity = data.item_info?.similarity;
          if (similarity === null || similarity === undefined || similarity < 100) return false;
        }

        // Confidence filters (only apply if not from memory and not validated)
        else {
          const similarity = data.item_info?.similarity;
          const isFromMemory = similarity !== null && similarity !== undefined && similarity >= 100;
          const isValidated = data.is_validated === true;
          if (isFromMemory || isValidated) return false; // Memory and validated items shouldn't show in confidence filters

          if (data.confidence === null || data.confidence === undefined) return false;

          const confidencePercent = data.confidence > 1 ? data.confidence : data.confidence * 100;
          // Updated ranges: Low (0-59), Medium (60-79), High (80-100)
          if (currentFilter.value === 'high') {
            if (confidencePercent < 80) return false;
          } else if (currentFilter.value === 'medium') {
            if (confidencePercent < 60 || confidencePercent >= 80) return false;
          } else if (currentFilter.value === 'low') {
            if (confidencePercent >= 60) return false;
          }
        }
      }

      // Handle status filter (processing)
      if (currentFilter.type === 'status') {
        if (currentFilter.value === 'processing') {
          if (data.async_task_status !== STATUS_PENDING && data.async_task_status !== STATUS_DOING) {
            return false;
          }
        }
      }
    }

    // Handle text filter
    if (translationTextFilter.trim() !== '') {
      const t = translationTextFilter.trim();
      let regex: RegExp | null = null;
      try {
        regex = new RegExp(t, 'i');
      } catch {
        regex = null;
      }

      const values = [
        String(data.id ?? ''),
        String(data.item_info?.source_text ?? ''),
        String(data.item_info?.source_dialect_code ?? ''),
        String(data.item_info?.target_text ?? ''),
        String(data.item_info?.target_dialect_code ?? ''),
      ];

      if (regex) {
        if (!values.some((value) => regex!.test(value))) return false;
      } else {
        const lowered = t.toLowerCase();
        if (!values.some((value) => value.toLowerCase().includes(lowered))) return false;
      }
    }

    return true;
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.async_task_status]: asyncTaskStatusRenderer,
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
      [IFields.is_validated]: validationStatusRenderer,
      [IFields.item_info]: translationItemInfoRenderer,
      [IFields.confidence]: translationConfidenceRenderer,
      [IFields.reasoning]: translationReasoningRenderer,
    }),
    [],
  );

  const handleActionTrigger = async (method: 'memory' | 'ai' | 'copy' | 'delete') => {
    // Set loading states immediately so user sees processing feedback
    itemsModel.loadingItem = true;
    itemsModel.loadingGrid = true;
    itemsModel.gridData = [];
    itemsModel.render();

    await preTranslateModel.preTranslate(method);
  };

  return (
    <Container>
      <ValidatorHeader
        id={projectId}
        name={projectName}
        idLabel="Project"
        bulkActions={
          <>
            {projectId > 0 && model.getCourseId() > 0 ? (
              <IdButton id={projectId} refId={IRefIds.SDL} courseId={model.getCourseId()} label="Open CAT" />
            ) : null}
            <TranslationProjectPreTranslateActions
              items={itemsModel.gridData}
              projectId={projectId}
              onActionTrigger={handleActionTrigger}
              disabled={preTranslateModel.running}
              awaitingMemoryCount={awaitingMemoryCount}
            />
          </>
        }
        enableMockCall={false}
        itemsModel={itemsModel}
      />

      <TranslationProjectPreTranslateSummary
        items={itemsModel.gridData}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      {awaitingMemoryCount > 0 && !preTranslateModel.running && (
        <InfoBanner>
          <strong>{awaitingMemoryCount} duplicate items</strong> are waiting. Validate the AI-translated originals, then run{' '}
          <strong>Memory Translation</strong> to fill them automatically.
        </InfoBanner>
      )}

      <FilterRow>
        <TextFieldStyled
          variant="outlined"
          size="small"
          placeholder="Filter by text / id / translation / reasoning"
          value={filterInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
        />
        <IconButton
          size="small"
          onClick={() => {
            setFilterInput('');
            setFilterText('');
          }}
          disabled={!filterInput}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
      </FilterRow>

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid cellRenderers={cellRenderers} isFilterActive={isFilterActive} isRowVisible={isRowVisible} />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default TranslationProjectPreTranslateDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const TextFieldStyled = styled(TextField)`
  flex-grow: 1;
`;

const InfoBanner = styled.div`
  padding: 10px 16px;
  background: #ECEFF1;
  border-left: 3px solid #78909C;
  font-size: 13px;
  color: #455A64;
  line-height: 1.4;
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
